import {
  Application,
  FillGradient,
  Graphics,
  Assets,
  Sprite,
  DisplacementFilter,
  Container,
} from "pixi.js"
import { sound } from '@pixi/sound';
import { Controls } from "./controls"
import { Player } from "./player"
import { World } from "./world/world"
import { Dust } from "./dust"
import { Hud } from "./hud"
import { APP_WIDTH, APP_HEIGHT } from "./constants"
import { Upgrades } from "./upgrades"

window.Game = {
  app: null,
}

const startGame = async () => {
  let app = null

  if (window.Game.app) {
    app = window.Game.app
    app.stage.removeChildren()
  } else {
    app = new Application()
    await app.init({
      background: "#112",
      width: APP_WIDTH,
      height: APP_HEIGHT,
      antialias: false,
    })

    document.getElementById("game").appendChild(app.canvas)
    app.stage.eventMode = "static"

    window.Game.app = app
  }

  sound.add('gun', 'res/gun-sound.mp3')
  sound.volume('gun', 0.06)

  sound.add('underwater', 'res/underwater.mp3')
  sound.volume('underwater', 0.03)
  sound.play('underwater', { loop: true })

  await Assets.load(["res/displacement-map.jpg", "res/controls.png"])

  const controls = Controls()

  const { player, tick: playerTick } = await Player({ controls })
  const {
    world,
    tick: worldTick,
    isSolid,
    spawnProjectile,
  } = await World({ player })
  window.Game.world = world

  const { dust, tick: dustTick } = await Dust({ player })
  const { vignette } = Vignette({ app })
  const { hud, tick: hudTick } = await Hud({ player })
  const { upgrades, openUpgrades } = await Upgrades({
    app,
    player,
  })

  player.openUpgrades = openUpgrades

  const filterContainer = new Container()
  const background = new Graphics()

  filterContainer.addChild(background)
  filterContainer.addChild(dust)
  filterContainer.addChild(world)
  filterContainer.addChild(vignette)
  filterContainer.addChild(hud)
  filterContainer.addChild(upgrades)

  const { filter, sprite: filterSprite, tick: filterTick } = Filter()
  filterContainer.addChild(filterSprite)
  filterContainer.filters = [filter]

  app.stage.addChild(filterContainer)

  let stopped = false
  app.ticker.add((time) => {
    if (app.paused || stopped) return

    background.clear()
    background
      .rect(0, 0, app.screen.width, app.screen.height)
      .fill(getBackgroundColor({ player }))

    playerTick(time, { isSolid, spawnProjectile })
    worldTick(time)
    dustTick(time)
    hudTick(time)
    filterTick(time)

    if (
      !upgrades.renderable &&
      player.health <= 0 &&
      controls.isKeyDown("Space")
    ) {
      stopped = true
      startGame()
    }
  })
}

;(async () => {
  await startGame()

  document
    .getElementById("game")
    .addEventListener("contextmenu", (event) => event.preventDefault())
})()

const getBackgroundColor = ({ player }) => {
  const start = [7, 29, 102] // Initial blue
  const middle = [0, 16, 60] // Dark blue
  const end = [20, 20, 20] // Dark gray
  const p = Math.max(0, Math.min(player.y / 10000, 1))

  let result
  if (p < 0.5) {
    // First half: blue to dark blue
    const p1 = p * 2
    result = start.map((s, i) => Math.round(s + (middle[i] - s) * p1))
  } else {
    // Second half: dark blue to dark gray
    const p2 = (p - 0.5) * 2
    result = middle.map((m, i) => Math.round(m + (end[i] - m) * p2))
  }

  return `#${result.map((c) => c.toString(16).padStart(2, "0")).join("")}`
}

const Vignette = ({ app }) => {
  const radialGradient = new FillGradient({
    type: "radial",
    center: { x: 0.5, y: 0.5 },
    innerRadius: 0,
    outerCenter: { x: 0.5, y: 0.5 },
    outerRadius: 0.5,
    colorStops: [
      { offset: 0, color: "#0000" }, // Center color
      { offset: 1, color: "#0003" }, // Edge color
    ],
    // Use normalized coordinate system where (0,0) is the top-left and (1,1) is the bottom-right of the shape
    textureSpace: "local",
  })

  const vignette = new Graphics()
    .rect(0, 0, app.screen.width, app.screen.height)
    .fill(radialGradient)

  return { vignette }
}

const Filter = () => {
  const sprite = Sprite.from("res/displacement-map.jpg")
  sprite.texture.source.addressMode = "repeat"
  const filter = new DisplacementFilter({ sprite, scale: { x: 20, y: 20 } })

  const tick = (time) => {
    sprite.y = (sprite.y + time.deltaTime * 1) % sprite.texture.height
  }

  return { filter, sprite, tick }
}
